import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { postEvent, useNativeMessagingContext } from "@WahooFitness/wahoo-offline-mfe";

export const REQUEST_RECOVERY_HEALTH_SOURCES_EVENT = "requestRecoveryHealthSources";

// Re-posts, measured from mount, until a valid reply lands. offline-mfe only makes a new listener live
// after its provider re-renders and re-subscribes, so a fast reply to the first post can be dropped.
export const RECOVERY_HEALTH_SOURCES_RETRY_DELAYS_MS = [1_500, 5_000];

export type RecoveryHealthSource = {
  // HKSource bundle identifier, stable across launches; the name is the athlete's and can change.
  id: string;
  name: string;
};

// Native's reply, sources sorted by name. `incomplete`: a query failed, so a device may be missing.
// `selectedId`: the device nights are built from, absent when none is resolved on this phone; it can be
// missing from `sources` (another phone's device, or one with nothing in the window).
// `hrvOnly`: `sources` holds only devices with HRV Wahoo can use (TAJ-1628), so an empty list means no
// device tracks HRV. An older app leaves it out and lists every device with recovery data.
// `accountDevice`: the account's HRV device (`recovery_vital_device_id`) whichever phone holds it, null
// when the account has none (TAJ-1641). An older app leaves it out.
export type RecoveryHealthSources = {
  sources: RecoveryHealthSource[];
  incomplete: boolean;
  selectedId?: string | null;
  hrvOnly?: boolean;
  accountDevice?: RecoveryAccountDevice | null;
};

// `name`: the name the device was registered under on the cloud; null when it couldn't be read, or when
// this phone holds the device (its row names it).
// `onThisPhone`: native's answer; false on another phone, and always on Android, which has no Apple Health.
export type RecoveryAccountDevice = {
  name: string | null;
  onThisPhone: boolean;
};

// Asks native again for the devices, syncing the account settings first: the account's device may have
// been changed on another phone since native last synced (TAJ-1641). Native answers at once from what it
// has, then again if the sync brings a different device; the reply reaches every mounted listener. The
// value is a string because the iOS host decodes eventData as [String: String].
export const refreshRecoveryHealthSources = () =>
  postEvent(REQUEST_RECOVERY_HEALTH_SOURCES_EVENT, { refresh: "true" });

// Asks native for the Apple Health devices with recovery data on every mount while enabled, so
// reopening the screen picks up a newly added device, or one chosen on another phone. Stays null when
// disabled, on web, on hosts without a handler (an Android app older than TAJ-1641), and on a malformed
// reply.
const useRecoveryHealthSources = (enabled: boolean): RecoveryHealthSources | null => {
  const { addEventResponseListener, removeEventResponseListener } = useNativeMessagingContext();
  const [recoveryHealthSources, setRecoveryHealthSources] = useState<RecoveryHealthSources | null>(
    null
  );

  useEffect(() => {
    if (!enabled || Capacitor.getPlatform() === "web") {
      return;
    }
    const retries: ReturnType<typeof setTimeout>[] = [];
    const listener = addEventResponseListener(
      REQUEST_RECOVERY_HEALTH_SOURCES_EVENT,
      ({ eventResponseData }) => {
        if (isRecoveryHealthSources(eventResponseData)) {
          retries.forEach(clearTimeout);
          setRecoveryHealthSources(eventResponseData);
        }
      }
    );
    refreshRecoveryHealthSources();
    RECOVERY_HEALTH_SOURCES_RETRY_DELAYS_MS.forEach((delay) => {
      retries.push(setTimeout(() => postEvent(REQUEST_RECOVERY_HEALTH_SOURCES_EVENT, {}), delay));
    });
    return () => {
      retries.forEach(clearTimeout);
      removeEventResponseListener(listener);
    };
  }, [enabled, addEventResponseListener, removeEventResponseListener]);

  return recoveryHealthSources;
};

const isRecoveryHealthSource = (value: unknown): value is RecoveryHealthSource =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as RecoveryHealthSource).id === "string" &&
  typeof (value as RecoveryHealthSource).name === "string";

const isRecoveryAccountDevice = (value: unknown): value is RecoveryAccountDevice =>
  typeof value === "object" &&
  value !== null &&
  ((value as RecoveryAccountDevice).name === null ||
    typeof (value as RecoveryAccountDevice).name === "string") &&
  typeof (value as RecoveryAccountDevice).onThisPhone === "boolean";

const isRecoveryHealthSources = (value: unknown): value is RecoveryHealthSources =>
  typeof value === "object" &&
  value !== null &&
  Array.isArray((value as RecoveryHealthSources).sources) &&
  (value as RecoveryHealthSources).sources.every(isRecoveryHealthSource) &&
  typeof (value as RecoveryHealthSources).incomplete === "boolean" &&
  ((value as RecoveryHealthSources).selectedId == null ||
    typeof (value as RecoveryHealthSources).selectedId === "string") &&
  ((value as RecoveryHealthSources).hrvOnly == null ||
    typeof (value as RecoveryHealthSources).hrvOnly === "boolean") &&
  ((value as RecoveryHealthSources).accountDevice == null ||
    isRecoveryAccountDevice((value as RecoveryHealthSources).accountDevice));

export default useRecoveryHealthSources;
